/**
 * ProfileRouteGuard — route-level gate for the shared self-serve My Profile
 * page (/crm/profile), Workstream B (STATUS-advisor-view-profile.md D-VP-4).
 *
 * Distinct from PackRouteGuard (which gates the rest of /crm/* on
 * hasOperatorPack and, on admit, flips currentMode → "operator_crm"). The
 * profile surface is capability-gated, not keyed to entry site or view mode, so
 * this guard:
 *
 *   - admits on hasFeature("user_profile") (advisor + senior_living +
 *     behavioral_health all declare it) instead of hasOperatorPack, so an
 *     advisor (lead_gen_only) user can reach their own profile; and
 *   - critically DOES NOT call setCurrentMode("operator_crm") — visiting your
 *     profile from Advisor view on a dual-pack tenant must not flip you into CRM
 *     view (that side effect is PackRouteGuard's, and it is intentionally absent
 *     here).
 *
 * Same loading-defer pattern as PackRouteGuard: while the installed-packs query
 * is in flight (external navigation / refresh / new tab), packs === [] and
 * hasFeature === false even for a tenant that DOES declare the feature. Deferring
 * the redirect decision until isLoading settles preserves the URL-survives-
 * refresh promise. The crm-skin visual wrapper stays in AppLayout (kept for both
 * entries).
 */
import { type ReactNode } from "react";
import { Navigate } from "react-router";
import { usePacks } from "./packs/PackContext";

export function ProfileRouteGuard({ children }: { children: ReactNode }) {
  const { hasFeature, isLoading } = usePacks();
  const allowed = hasFeature("user_profile");

  if (isLoading) {
    return (
      <div
        className="p-6 flex justify-center"
        role="status"
        aria-busy="true"
        aria-live="polite"
        aria-label="Loading profile access"
      >
        <div
          className="h-8 w-8 rounded-full border-2 border-gray-200 border-t-gray-500 animate-spin"
          aria-hidden="true"
        />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (!allowed) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }
  return <>{children}</>;
}
